// Firebase adapter that preserves the historical `supabase` interface used in the app.
// This allows a progressive migration without rewriting every module immediately.
import {
  getRedirectResult, signInWithPopup, signInWithRedirect, signOut as firebaseSdkSignOut,
  sendSignInLinkToEmail, isSignInWithEmailLink as firebaseIsSignInWithEmailLink, signInWithEmailLink,
} from 'firebase/auth';
import { firebaseAuth, firebaseGoogleProvider } from './firebase';

// Passwordless email-link ("magic link") sign-in. Additive: password and
// Google auth are untouched. Firebase requires the email that requested the
// link to complete it — when the visitor opens the link on the same
// browser/device this is transparent (read back from localStorage in
// completeEmailLinkSignIn below); on a different device, completion fails
// with a clear error rather than guessing an identity.
const EMAIL_LINK_STORAGE_KEY = 'nsara_email_link_pending';

const FIREBASE_API_KEY = process.env.REACT_APP_FIREBASE_API_KEY || '';
const FIREBASE_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
const FIREBASE_AUTH_DOMAIN = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '';

const FIREBASE_ENABLED = Boolean(FIREBASE_API_KEY && FIREBASE_PROJECT_ID);

const SESSION_KEY = 'nsara_firebase_session';
const AUTH_EVENT_KEY = 'nsara_firebase_auth_event';

const authListeners = new Set();

const firestoreBaseUrl = FIREBASE_ENABLED
  ? `https://firestore.googleapis.com/v1/projects/${FIREBASE_PROJECT_ID}/databases/(default)/documents`
  : '';

const authBaseUrl = FIREBASE_ENABLED
  ? `https://identitytoolkit.googleapis.com/v1/accounts`
  : '';
const secureTokenBaseUrl = FIREBASE_ENABLED
  ? 'https://securetoken.googleapis.com/v1'
  : '';

const parseJsonSafe = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const readSession = () => {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  return parseJsonSafe(raw, null);
};

const writeSession = (session) => {
  if (!session) {
    localStorage.removeItem(SESSION_KEY);
    return;
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
};

const dispatchAuthEvent = (event, session) => {
  localStorage.setItem(AUTH_EVENT_KEY, String(Date.now()));
  authListeners.forEach((cb) => cb(event, session));
};

// Buffer subtracted from the token's real expiry so a request that starts
// just before expiry doesn't race a rejection: session-level expiry is
// treated as reached slightly before Firebase's own clock would say so.
const EXPIRY_SAFETY_MARGIN_MS = 60000;

const buildSession = (payload) => {
  if (!payload?.localId || !payload?.idToken) return null;
  const resolvedName = payload.displayName || payload.name || payload.email || '';
  const resolvedPhoto = payload.photoUrl || payload.photoURL || payload.picture || null;
  const expiresInMs = Number(payload.expiresIn || 3600) * 1000;
  return {
    access_token: payload.idToken,
    refresh_token: payload.refreshToken,
    expires_in: payload.expiresIn,
    expires_at: Date.now() + expiresInMs,
    user: {
      id: payload.localId,
      email: payload.email || '',
      user_metadata: {
        nom: resolvedName,
        name: resolvedName,
        avatar_url: resolvedPhoto,
      },
    },
  };
};

const buildSessionFromFirebaseUser = async (firebaseUser) => {
  if (!firebaseUser?.uid) return null;
  const token = await firebaseUser.getIdToken();
  return {
    access_token: token,
    refresh_token: firebaseUser?.refreshToken || '',
    expires_in: 3600,
    expires_at: Date.now() + 3600 * 1000,
    user: {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      // P-AUTH-EMAIL-VERIFY-2: Firebase's own authoritative signal, taken
      // as-is from the SDK user object. Google and completed Magic Link
      // sign-ins are always true here (Firebase sets this itself) — no
      // provider-name inference is used anywhere for this.
      emailVerified: Boolean(firebaseUser.emailVerified),
      user_metadata: {
        nom: firebaseUser.displayName || firebaseUser.email || '',
        name: firebaseUser.displayName || firebaseUser.email || '',
        avatar_url: firebaseUser.photoURL || null,
      },
    },
  };
};

// The one place that decides "this cached token is too old to trust" — used
// both to refresh proactively before a request (getAuthHeadersWithRecovery)
// and to decide whether a 401 is worth retrying (apiClient.js).
const isSessionExpired = (session) => {
  if (!session?.access_token) return true;
  const expiresAt = Number(session.expires_at || 0);
  if (!expiresAt) return false; // legacy cached session predating this field: let the request itself decide.
  return Date.now() >= expiresAt - EXPIRY_SAFETY_MARGIN_MS;
};

// A session whose refresh token is exhausted/revoked: there is nothing left
// to retry. Clears the cached session and notifies every onAuthStateChange
// listener (this is exactly how signOut() already announces a logout — see
// `auth.signOut` below — so AuthContext's existing handler, which already
// clears currentUser on a null session, needs no changes to react to this).
const clearInvalidSession = () => {
  writeSession(null);
  dispatchAuthEvent('SIGNED_OUT', null);
  try { window.sessionStorage.setItem('nsara_session_expired', '1'); } catch (e) {}
};

const syncSessionFromFirebaseAuth = async () => {
  try {
    const redirectResult = await getRedirectResult(firebaseAuth).catch(() => null);
    const firebaseUser = redirectResult?.user || firebaseAuth.currentUser || null;
    if (!firebaseUser) return null;
    const session = await buildSessionFromFirebaseUser(firebaseUser);
    writeSession(session);
    return session;
  } catch (e) {
    return null;
  }
};

const parseJsonResponse = async (res) => {
  const data = await res.json().catch(() => ({}));
  return data;
};

// Firebase's password-sign-in response contains identity and tokens but not
// profile fields such as photoUrl. Keep the adapter's cached session aligned
// with the persisted Firebase profile after every authentication path.
const fetchFirebaseProfile = async (idToken) => {
  if (!FIREBASE_ENABLED || !idToken) return null;
  try {
    const lookupRes = await fetch(`${authBaseUrl}:lookup?key=${FIREBASE_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken })
    });
    const lookupPayload = await parseJsonResponse(lookupRes);
    if (!lookupRes.ok || lookupPayload?.error) return null;
    return Array.isArray(lookupPayload?.users) ? lookupPayload.users[0] || null : null;
  } catch (e) {
    // A profile lookup must not turn an otherwise valid authentication into a failed login.
    return null;
  }
};

const hydrateSessionProfile = async (session) => {
  if (!session?.access_token) return session;
  const profile = await fetchFirebaseProfile(session.access_token);
  // Fails closed on purpose: if the profile lookup itself fails (network
  // issue, etc.), the session's emailVerified stays whatever it already
  // was rather than being assumed true — safer for a security gate than
  // silently trusting a stale/default value.
  if (!profile) return session;

  const existingMetadata = session.user?.user_metadata || {};
  const persistedName = String(profile.displayName || '').trim();
  const resolvedName = persistedName || existingMetadata.nom || existingMetadata.name || session.user?.email || '';
  const resolvedPhoto = profile.photoUrl ?? existingMetadata.avatar_url ?? null;

  return {
    ...session,
    user: {
      ...session.user,
      // P-AUTH-EMAIL-VERIFY-2: accounts:lookup's response is Firebase's
      // authoritative account state — this is how both fresh signups
      // (always false) and re-checks after clicking the verification link
      // (true once Firebase has processed it) get the correct value.
      emailVerified: Boolean(profile.emailVerified),
      user_metadata: {
        ...existingMetadata,
        nom: resolvedName,
        name: resolvedName,
        avatar_url: resolvedPhoto,
      },
    },
  };
};

const isAuthErrorMessage = (message = '') => {
  const normalized = String(message || '').toLowerCase();
  return normalized.includes('missing or invalid authentication')
    || normalized.includes('invalid_id_token')
    || normalized.includes('token expired')
    || normalized.includes('permission_denied');
};

let refreshInFlight = null;
const refreshSessionToken = async () => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const session = readSession();
    const refreshToken = String(session?.refresh_token || '').trim();
    if (!refreshToken) throw new Error('Refresh token introuvable');

    const tokenRes = await fetch(`${secureTokenBaseUrl}/token?key=${FIREBASE_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken
      }).toString()
    });
    const tokenPayload = await parseJsonResponse(tokenRes);
    if (!tokenRes.ok || tokenPayload?.error) {
      throw new Error(tokenPayload?.error?.message || `HTTP ${tokenRes.status}`);
    }

    const nextSession = await hydrateSessionProfile(buildSession({
      localId: tokenPayload.user_id || session?.user?.id,
      idToken: tokenPayload.id_token,
      refreshToken: tokenPayload.refresh_token || refreshToken,
      expiresIn: tokenPayload.expires_in || 3600,
      email: session?.user?.email || '',
      displayName: session?.user?.user_metadata?.name || '',
      photoUrl: session?.user?.user_metadata?.avatar_url ?? null
    }));
    writeSession(nextSession);
    dispatchAuthEvent('TOKEN_REFRESHED', nextSession);
    return nextSession;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};

const firebaseFetch = async (url, options = {}, allowRetry = true) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.error) {
    const message = data?.error?.message || `HTTP ${res.status}`;
    const shouldTryRefresh =
      allowRetry &&
      FIREBASE_ENABLED &&
      isAuthErrorMessage(message) &&
      !String(url).includes('/token?key=');

    if (shouldTryRefresh) {
      try {
        const refreshed = await refreshSessionToken();
        const hadAuthHeader = Boolean(options?.headers?.Authorization || options?.headers?.authorization);
        const isFirestoreRequest = String(url).startsWith(firestoreBaseUrl);
        const headers = {
          ...(options?.headers || {}),
          ...(isFirestoreRequest || hadAuthHeader ? { Authorization: `Bearer ${refreshed?.access_token || ''}` } : {})
        };
        return firebaseFetch(url, { ...options, headers }, false);
      } catch (e) {
        throw new Error(e?.message || message);
      }
    }
    throw new Error(message);
  }
  return data;
};

const normalizeEmail = (value) => {
  if (typeof value === 'string') return value.trim().toLowerCase();
  if (value && typeof value === 'object') {
    if (typeof value.email === 'string') return value.email.trim().toLowerCase();
    if (typeof value.value === 'string') return value.value.trim().toLowerCase();
  }
  return String(value || '').trim().toLowerCase();
};

const normalizePassword = (value) => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    if (typeof value.password === 'string') return value.password;
    if (typeof value.value === 'string') return value.value;
  }
  return String(value || '');
};

const toFirestoreValue = (value) => {
  if (value === null || value === undefined) return { nullValue: null };
  if (Array.isArray(value)) return { arrayValue: { values: value.map(toFirestoreValue) } };
  if (typeof value === 'boolean') return { booleanValue: value };
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return { integerValue: String(value) };
    return { doubleValue: value };
  }
  if (typeof value === 'string') return { stringValue: value };
  if (typeof value === 'object') {
    const fields = Object.entries(value).reduce((acc, [k, v]) => {
      acc[k] = toFirestoreValue(v);
      return acc;
    }, {});
    return { mapValue: { fields } };
  }
  return { stringValue: String(value) };
};

const fromFirestoreValue = (value) => {
  if (!value || typeof value !== 'object') return null;
  if ('nullValue' in value) return null;
  if ('stringValue' in value) return value.stringValue;
  if ('booleanValue' in value) return Boolean(value.booleanValue);
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('arrayValue' in value) return (value.arrayValue.values || []).map(fromFirestoreValue);
  if ('mapValue' in value) {
    const fields = value.mapValue.fields || {};
    return Object.entries(fields).reduce((acc, [k, v]) => {
      acc[k] = fromFirestoreValue(v);
      return acc;
    }, {});
  }
  return null;
};

const decodeFirestoreDoc = (doc) => {
  const id = String(doc?.name || '').split('/').pop();
  const fields = doc?.fields || {};
  const data = Object.entries(fields).reduce((acc, [k, v]) => {
    acc[k] = fromFirestoreValue(v);
    return acc;
  }, {});
  return { id, ...data };
};

const encodeFirestoreData = (data) => {
  const fields = Object.entries(data || {}).reduce((acc, [k, v]) => {
    acc[k] = toFirestoreValue(v);
    return acc;
  }, {});
  return { fields };
};

// Exporté pour frontend/src/services/apiClient.js: toute requête vers le backend
// NSARA doit joindre ce header plutôt que d'envoyer un email/userId "de confiance".
//
// P0 fix: cette fonction se contentait auparavant de relire le token en cache
// SANS jamais regarder s'il avait expiré (les tokens Firebase expirent après
// 1h). Résultat en production: l'UI restait "connectée" (currentUser vient
// du cache local, indépendant du réseau) alors que chaque appel API échouait
// avec "Session invalide ou expirée" — Dashboard, boutiques, création de
// boutique, tout ce qui passe par apiFetch. Le rafraîchissement automatique
// existait déjà (refreshSessionToken, utilisé par firebaseFetch) mais
// uniquement pour les appels Firebase eux-mêmes, jamais pour le backend
// NSARA. Cette fonction rafraîchit maintenant proactivement un token expiré
// avant de le renvoyer; apiClient.js gère en complément le cas résiduel où
// le backend rejette quand même (retry unique, voir apiClient.js).
export const getAuthHeadersWithRecovery = async () => {
  let session = readSession();
  if (!session?.access_token) {
    session = await syncSessionFromFirebaseAuth();
  }
  if (!session?.access_token && firebaseAuth?.currentUser) {
    session = await buildSessionFromFirebaseUser(firebaseAuth.currentUser).catch(() => null);
    if (session?.access_token) writeSession(session);
  }
  if (session?.access_token && isSessionExpired(session)) {
    try {
      session = await refreshSessionToken();
    } catch (e) {
      // Refresh token itself is invalid/revoked: nothing left to retry.
      clearInvalidSession();
      return {};
    }
  }
  const token = String(session?.access_token || '').trim();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Exposed so apiClient.js can force one refresh-and-retry when the backend
// rejects a token this function believed was still valid (clock skew, or a
// legacy cached session with no expires_at yet — see isSessionExpired).
export const forceRefreshSession = async () => {
  try {
    return await refreshSessionToken();
  } catch (e) {
    clearInvalidSession();
    return null;
  }
};

const getCollectionDocs = async (table) => {
  if (!FIREBASE_ENABLED) return [];
  const url = `${firestoreBaseUrl}/${table}`;
  const headers = await getAuthHeadersWithRecovery();
  const data = await firebaseFetch(url, { headers });
  const docs = Array.isArray(data?.documents) ? data.documents : [];
  return docs.map(decodeFirestoreDoc);
};

const getDocById = async (table, id) => {
  if (!FIREBASE_ENABLED || !id) return null;
  const docId = encodeURIComponent(String(id));
  const url = `${firestoreBaseUrl}/${table}/${docId}`;
  const headers = await getAuthHeadersWithRecovery();
  const data = await firebaseFetch(url, { headers });
  return decodeFirestoreDoc(data);
};

const createDoc = async (table, payload) => {
  const docId = payload?.id ? String(payload.id) : undefined;
  const cleanPayload = { ...(payload || {}) };
  if (cleanPayload.id) delete cleanPayload.id;

  const query = docId ? `?documentId=${encodeURIComponent(docId)}` : '';
  const url = `${firestoreBaseUrl}/${table}${query}`;
  const authHeaders = await getAuthHeadersWithRecovery();
  const created = await firebaseFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(encodeFirestoreData(cleanPayload)),
  });
  return decodeFirestoreDoc(created);
};

const updateDoc = async (table, id, payload) => {
  const docId = encodeURIComponent(String(id));
  const url = `${firestoreBaseUrl}/${table}/${docId}`;
  const cleanPayload = { ...(payload || {}) };
  if (cleanPayload.id) delete cleanPayload.id;
  const authHeaders = await getAuthHeadersWithRecovery();

  const updated = await firebaseFetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify(encodeFirestoreData(cleanPayload)),
  });
  return decodeFirestoreDoc(updated);
};

const deleteDoc = async (table, id) => {
  const docId = encodeURIComponent(String(id));
  const url = `${firestoreBaseUrl}/${table}/${docId}`;
  const authHeaders = await getAuthHeadersWithRecovery();
  await firebaseFetch(url, {
    method: 'DELETE',
    headers: authHeaders,
  });
  return true;
};

class QueryBuilder {
  constructor(table) {
    this.table = table;
    this.filters = [];
    this.limitCount = null;
    this.orderBy = null;
    this.expectSingle = false;
    this.operation = 'select';
    this.insertPayload = null;
    this.updatePayload = null;
    this.upsertPayload = null;
    this.exactId = null;
  }

  select() {
    if (this.operation === 'insert' || this.operation === 'update' || this.operation === 'upsert' || this.operation === 'delete') {
      return this;
    }
    this.operation = 'select';
    return this;
  }

  insert(payload) {
    this.operation = 'insert';
    this.insertPayload = payload;
    return this;
  }

  update(payload) {
    this.operation = 'update';
    this.updatePayload = payload;
    return this;
  }

  upsert(payload) {
    this.operation = 'upsert';
    this.upsertPayload = payload;
    return this;
  }

  delete() {
    this.operation = 'delete';
    return this;
  }

  eq(field, value) {
    if (field === 'id') {
      this.exactId = value;
    }
    this.filters.push((row) => {
      const direct = row?.[field] === value;
      if (direct) return true;
      // Compatibilité entre schémas snake_case/camelCase selon les écritures historiques.
      if (field === 'user_id') return row?.userId === value;
      if (field === 'userId') return row?.user_id === value;
      return false;
    });
    return this;
  }

  gt(field, value) {
    this.filters.push((row) => Number(row?.[field] || 0) > Number(value || 0));
    return this;
  }

  order(field, options = {}) {
    this.orderBy = { field, ascending: options?.ascending !== false };
    return this;
  }

  limit(count) {
    this.limitCount = Number(count || 0);
    return this;
  }

  single() {
    this.expectSingle = true;
    return this;
  }

  _applyFilters(rows) {
    let output = rows;
    this.filters.forEach((fn) => {
      output = output.filter(fn);
    });

    if (this.orderBy?.field) {
      const { field, ascending } = this.orderBy;
      output = [...output].sort((a, b) => {
        const av = a?.[field];
        const bv = b?.[field];
        if (av === bv) return 0;
        if (av === undefined || av === null) return ascending ? 1 : -1;
        if (bv === undefined || bv === null) return ascending ? -1 : 1;
        return ascending ? (av > bv ? 1 : -1) : (av > bv ? -1 : 1);
      });
    }

    if (this.limitCount && this.limitCount > 0) {
      output = output.slice(0, this.limitCount);
    }

    return output;
  }

  async execute() {
    if (!FIREBASE_ENABLED) {
      return { data: this.expectSingle ? null : [], error: new Error('Firebase non configuré') };
    }

    try {
      if (this.operation === 'select') {
        const docs = this.exactId
          ? [await getDocById(this.table, this.exactId)].filter(Boolean)
          : await getCollectionDocs(this.table);
        const filtered = this._applyFilters(docs);
        return { data: this.expectSingle ? (filtered[0] || null) : filtered, error: null };
      }

      if (this.operation === 'insert') {
        const rows = Array.isArray(this.insertPayload) ? this.insertPayload : [this.insertPayload];
        const inserted = [];
        for (const row of rows) {
          inserted.push(await createDoc(this.table, row));
        }
        return { data: this.expectSingle ? (inserted[0] || null) : inserted, error: null };
      }

      if (this.operation === 'update') {
        const docs = this.exactId
          ? [await updateDoc(this.table, this.exactId, this.updatePayload || {})]
          : this._applyFilters(await getCollectionDocs(this.table));
        const updated = [];
        if (this.exactId) {
          updated.push(...docs);
        } else {
          for (const doc of docs) {
            updated.push(await updateDoc(this.table, doc.id, this.updatePayload || {}));
          }
        }
        return { data: this.expectSingle ? (updated[0] || null) : updated, error: null };
      }

      if (this.operation === 'upsert') {
        const rows = Array.isArray(this.upsertPayload) ? this.upsertPayload : [this.upsertPayload];
        const merged = [];
        for (const row of rows) {
          if (row?.id) {
            merged.push(await updateDoc(this.table, row.id, row));
          } else {
            const existing = this._applyFilters(await getCollectionDocs(this.table));
            if (existing[0]?.id) {
              merged.push(await updateDoc(this.table, existing[0].id, row || {}));
            } else {
              merged.push(await createDoc(this.table, row));
            }
          }
        }
        return { data: this.expectSingle ? (merged[0] || null) : merged, error: null };
      }

      if (this.operation === 'delete') {
        const docs = this.exactId
          ? [await getDocById(this.table, this.exactId)].filter(Boolean)
          : this._applyFilters(await getCollectionDocs(this.table));
        for (const doc of docs) {
          await deleteDoc(this.table, doc.id);
        }
        return { data: docs, error: null };
      }

      return { data: this.expectSingle ? null : [], error: null };
    } catch (error) {
      return { data: this.expectSingle ? null : [], error };
    }
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

const auth = {
  onAuthStateChange(callback) {
    authListeners.add(callback);
    const session = readSession();
    setTimeout(() => callback('INITIAL_SESSION', session), 0);

    const onStorage = (event) => {
      if (event?.key === AUTH_EVENT_KEY || event?.key === SESSION_KEY) {
        callback('TOKEN_REFRESHED', readSession());
      }
    };
    window.addEventListener('storage', onStorage);

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            authListeners.delete(callback);
            window.removeEventListener('storage', onStorage);
          },
        },
      },
    };
  },

  async getSession() {
    let session = readSession();
    if (!session) {
      session = await syncSessionFromFirebaseAuth();
      if (session) dispatchAuthEvent('SIGNED_IN', session);
    }
    if (session) {
      const hydratedSession = await hydrateSessionProfile(session);
      if (hydratedSession !== session) {
        session = hydratedSession;
        writeSession(session);
      }
    }
    return { data: { session } };
  },

  async getUser() {
    const session = readSession();
    return { data: { user: session?.user || null } };
  },

  async signInWithPassword({ email, password }) {
    if (!FIREBASE_ENABLED) return { data: null, error: new Error('Firebase non configuré') };

    try {
      const payload = await firebaseFetch(`${authBaseUrl}:signInWithPassword?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizeEmail(email),
          password: normalizePassword(password),
          returnSecureToken: true
        }),
      });
      const session = await hydrateSessionProfile(buildSession(payload));
      writeSession(session);
      dispatchAuthEvent('SIGNED_IN', session);
      return { data: { user: session?.user, session }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signUp({ email, password, options = {} }) {
    if (!FIREBASE_ENABLED) return { data: null, error: new Error('Firebase non configuré') };

    try {
      const payload = await firebaseFetch(`${authBaseUrl}:signUp?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizeEmail(email),
          password: normalizePassword(password),
          returnSecureToken: true
        }),
      });

      if (options?.data?.name) {
        await firebaseFetch(`${authBaseUrl}:update?key=${FIREBASE_API_KEY}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: payload.idToken, displayName: options.data.name, returnSecureToken: true }),
        }).catch(() => ({}));
      }

      // P-AUTH-EMAIL-VERIFY-2: request Firebase's built-in verification
      // email for the account that was just created, authenticated with
      // its own fresh idToken (never an arbitrary email — this is the same
      // sendOobCode endpoint resetPasswordForEmail already uses below, just
      // with requestType VERIFY_EMAIL). Best-effort on purpose: the account
      // already exists at this point, so a failure here must not fail
      // signup itself — the pending-verification UI's resend action is the
      // recovery path for that case.
      await firebaseFetch(`${authBaseUrl}:sendOobCode?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestType: 'VERIFY_EMAIL', idToken: payload.idToken }),
      }).catch(() => {});

      // hydrateSessionProfile re-reads the account via accounts:lookup,
      // which is what actually carries emailVerified — the signUp response
      // itself doesn't. A brand-new account is always false here; this call
      // is what makes that authoritative rather than assumed.
      const session = await hydrateSessionProfile(buildSession({ ...payload, displayName: options?.data?.name || '' }));
      writeSession(session);
      dispatchAuthEvent('SIGNED_IN', session);
      return { data: { user: session?.user, session }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signOut() {
    try {
      await firebaseSdkSignOut(firebaseAuth);
    } catch (e) {}
    writeSession(null);
    dispatchAuthEvent('SIGNED_OUT', null);
    return { error: null };
  },

  async resetPasswordForEmail(email) {
    if (!FIREBASE_ENABLED) return { error: new Error('Firebase non configuré') };
    try {
      await firebaseFetch(`${authBaseUrl}:sendOobCode?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestType: 'PASSWORD_RESET', email: normalizeEmail(email) }),
      });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // P-AUTH-EMAIL-VERIFY-2: re-reads the CURRENT authenticated session's own
  // Firebase account state (never an arbitrary email) and updates the
  // cached session with whatever Firebase now reports — this is how
  // "J'ai vérifié mon adresse" observes a link the person already clicked.
  async refreshUserProfile() {
    const session = readSession();
    if (!session?.access_token) return { data: { session: null }, error: new Error('Session introuvable') };
    try {
      const hydrated = await hydrateSessionProfile(session);
      writeSession(hydrated);
      dispatchAuthEvent('USER_UPDATED', hydrated);
      return { data: { session: hydrated }, error: null };
    } catch (error) {
      return { data: { session }, error };
    }
  },

  // Resends Firebase's verification e-mail for the CURRENTLY authenticated
  // session — identity comes from its idToken, never a user-supplied
  // address, so this can never be used to "verify" an address the caller
  // doesn't already hold a live session for.
  async sendEmailVerification() {
    if (!FIREBASE_ENABLED) return { error: new Error('Firebase non configuré') };
    const session = readSession();
    if (!session?.access_token) return { error: new Error('Session introuvable') };
    try {
      await firebaseFetch(`${authBaseUrl}:sendOobCode?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestType: 'VERIFY_EMAIL', idToken: session.access_token }),
      });
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async updateUser({ data = {} } = {}) {
    if (!FIREBASE_ENABLED) return { data: null, error: new Error('Firebase non configuré') };
    const session = readSession();
    if (!session?.access_token) return { data: null, error: new Error('Session introuvable') };
    try {
      const nextDisplayName = String(data?.nom || data?.name || '').trim();
      const nextPhotoUrl = typeof data?.photoURL === 'string' ? data.photoURL.trim() : undefined;
      const payload = {
        idToken: session.access_token,
        returnSecureToken: true
      };
      if (nextDisplayName) payload.displayName = nextDisplayName;
      if (nextPhotoUrl !== undefined) payload.photoUrl = nextPhotoUrl;

      const updated = await firebaseFetch(`${authBaseUrl}:update?key=${FIREBASE_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const merged = buildSession({
        ...updated,
        localId: updated.localId || session?.user?.id,
        email: updated.email || session?.user?.email || '',
        displayName: updated.displayName || nextDisplayName || session?.user?.user_metadata?.name || '',
        photoUrl: (updated.photoUrl ?? nextPhotoUrl ?? session?.user?.user_metadata?.avatar_url ?? null)
      });
      writeSession(merged);
      dispatchAuthEvent('USER_UPDATED', merged);
      return { data: { user: merged?.user, session: merged }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async refreshSession() {
    if (!FIREBASE_ENABLED) return { data: { session: null }, error: new Error('Firebase non configuré') };
    try {
      const nextSession = await refreshSessionToken();
      return { data: { session: nextSession, user: nextSession?.user || null }, error: null };
    } catch (error) {
      return { data: { session: null }, error };
    }
  },

  // Envoie un lien de connexion sans mot de passe à l'adresse fournie
  // (nommé signInWithOtp pour rester cohérent avec la façade "supabase" déjà
  // utilisée dans ce fichier — c'est le nom réel de l'équivalent côté
  // Supabase). Additif: n'affecte ni signInWithPassword ni signInWithOAuth.
  async signInWithOtp({ email } = {}) {
    if (!FIREBASE_ENABLED) return { data: null, error: new Error('Connexion par e-mail non configurée.') };
    const safeEmail = normalizeEmail(email);
    if (!safeEmail) return { data: null, error: new Error('Adresse e-mail requise.') };
    try {
      await sendSignInLinkToEmail(firebaseAuth, safeEmail, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });
      try { window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, safeEmail); } catch (e) {}
      return { data: {}, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Vrai si l'URL courante est un lien de connexion Firebase valide (l'appelant
  // décide quoi en faire — voir Login.js, qui tente completeEmailLinkSignIn).
  isSignInWithEmailLink(url = window.location.href) {
    return FIREBASE_ENABLED && firebaseIsSignInWithEmailLink(firebaseAuth, url);
  },

  async completeEmailLinkSignIn(url = window.location.href) {
    if (!FIREBASE_ENABLED) return { data: null, error: new Error('Connexion par e-mail non configurée.') };
    let email = '';
    try { email = window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY) || ''; } catch (e) {}
    if (!email) {
      return { data: null, error: new Error("Ce lien doit être ouvert sur l'appareil et le navigateur utilisés pour le demander.") };
    }
    try {
      const result = await signInWithEmailLink(firebaseAuth, email, url);
      try { window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY); } catch (e) {}
      const session = await buildSessionFromFirebaseUser(result.user);
      writeSession(session);
      dispatchAuthEvent('SIGNED_IN', session);
      return { data: { user: session?.user, session }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async signInWithOAuth({ provider } = {}) {
    if (provider !== 'google') {
      return { data: null, error: new Error('Provider OAuth non supporté') };
    }
    try {
      // P-AUTH-HOTFIX-2B: there used to be an `await firebaseSdkSignOut(...)`
      // here before the popup call. Removed — a successful Google sign-in
      // already fully supersedes whatever Firebase user/session existed
      // before it (signInWithPopup doesn't link/merge, it just authenticates
      // as the new Google account), so the cleanup had no correctness
      // benefit. Worse, that `await` inserted an async gap between the
      // user's tap and `signInWithPopup`'s internal `window.open` — mobile
      // browsers are far stricter than desktop about treating `window.open`
      // as a trusted user gesture, and anything after an `await` risks the
      // popup being silently blocked without the `auth/popup-blocked` error
      // code the fallback below watches for. The popup call must now stay
      // the very first async operation on this path.
      let credentialUser = null;
      try {
        const popupResult = await signInWithPopup(firebaseAuth, firebaseGoogleProvider);
        credentialUser = popupResult?.user || null;
      } catch (popupError) {
        const code = String(popupError?.code || '');
        // Only a genuinely blocked popup justifies a full-page redirect.
        // `popup-closed-by-user` means the person deliberately closed the
        // window — forcing them straight into a Google redirect right after
        // that would be a worse experience than just letting them retry.
        // `cancelled-popup-request` means a second concurrent attempt got
        // auto-cancelled by Firebase — it says nothing about whether popups
        // work here, so it isn't a reason to redirect either.
        const shouldRedirect = code.includes('popup-blocked');
        if (!shouldRedirect) throw popupError;

        // signInWithRedirect() navigates the browser away to Google; this
        // page's JS stops mattering the moment that navigation lands. The
        // only place that can ever observe the outcome is the NEXT page
        // load, via syncSessionFromFirebaseAuth() → getRedirectResult()
        // (already wired into getSession(), called on every AuthProvider
        // mount) — calling getRedirectResult() here, in the same
        // invocation, could only ever see "no redirect pending yet" and
        // was a no-op. Deliberately never resolve/reject after initiating
        // the redirect: doing so would race the actual navigation and let
        // the caller (Login.js/Register.js) briefly treat this as a
        // finished success or failure before the page has even left.
        await signInWithRedirect(firebaseAuth, firebaseGoogleProvider);
        await new Promise(() => {});
      }

      if (!credentialUser) throw new Error('Connexion Google interrompue');
      const session = await buildSessionFromFirebaseUser(credentialUser);
      writeSession(session);
      dispatchAuthEvent('SIGNED_IN', session);
      return { data: { user: session?.user, session }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  admin: {
    async listUsers() {
      return { data: { users: [] }, error: null };
    },
  },
};

export const supabase = {
  from(table) {
    return new QueryBuilder(table);
  },
  auth,
};

export const testSupabaseConnection = async () => {
  if (!FIREBASE_ENABLED) return { success: false, message: 'Firebase non configuré' };
  try {
    await getCollectionDocs('profiles').catch(() => []);
    return { success: true };
  } catch (err) {
    return { success: false, message: err?.message || 'Erreur connexion Firebase' };
  }
};

export const firebaseInfo = {
  enabled: FIREBASE_ENABLED,
  apiKeyConfigured: Boolean(FIREBASE_API_KEY),
  projectIdConfigured: Boolean(FIREBASE_PROJECT_ID),
  authDomain: FIREBASE_AUTH_DOMAIN,
};

export default supabase;
